import React from 'react';
import { Field, Form, Formik } from 'formik';
import { Theme } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';

import { required } from '../../../utils/validation';
import { FormikFieldRenderProps } from '@troy/shared/src/types/formik';
import {
  ActionButton,
  Translation,
  TextInput
} from '@troy/shared/src/components/common';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%'
  },
  newPassword: {
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  confirmPassword: {
    width: '100%',
    marginBottom: theme.spacing(2)
  },
  confirmButton: {
    width: `calc(100% - ${theme.spacing(4)})`
  },
  forgotPassword: {
    marginTop: theme.spacing(4),
    whiteSpace: 'pre',
    textAlign: 'center',
    letterSpacing: '0.6px'
  }
}));

interface NewPasswordFormProps {
  onSubmit: (values: { newPassword: string; confirmPassword: string }) => void;
}

const NewPassword: React.FC<FormikFieldRenderProps> = ({
  field: { name, value, onChange }
}): React.ReactElement => {
  const classes = useStyles();

  return (
    <TextInput
      className={classes.newPassword}
      id="new-password-form-new-password"
      placeholder="New Password"
      inputPlaceholder="ALL_UXCOPIES_PLACEHOLDER_PASSWORD"
      startIcon="key"
      type="password"
      name={name}
      value={value}
      onChange={onChange}
    />
  );
};

const ConfirmPassword: React.FC<FormikFieldRenderProps> = ({
                                                         field: { name, value, onChange }
                                                       }): React.ReactElement => {
  const classes = useStyles();

  return (
      <TextInput
          className={classes.confirmPassword}
          id="new-password-form-confirm-password"
          placeholder="Password Again"
          inputPlaceholder="ALL_UXCOPIES_PLACEHOLDER_PASSWORD"
          startIcon="key"
          type="password"
          name={name}
          value={value}
          onChange={onChange}
      />
  );
};

const NewPasswordForm: React.FC<NewPasswordFormProps> = ({ onSubmit }) => {
  const classes = useStyles();

  return (
    <Formik
      validateOnMount
      initialValues={{ newPassword: '', confirmPassword: '' }}
      onSubmit={onSubmit}
      validate={(values) => {
        const errors: Record<string, string> = {};

        if (!values.newPassword) {
          errors.newPassword = 'Password is required';
        } else if (values.newPassword.length < 8) {
          errors.newPassword = 'Password must be at least 8 characters';
        } else if (!/[a-z]/.test(values.newPassword)) {
          errors.newPassword = 'Must include at least one lowercase letter';
        } else if (!/[A-Z]/.test(values.newPassword)) {
          errors.newPassword = 'Must include at least one uppercase letter';
        }

        if (!values.confirmPassword) {
          errors.confirmPassword = 'Please confirm your password';
        } else if (values.confirmPassword !== values.newPassword) {
          errors.confirmPassword = 'Passwords do not match';
        }

        return errors;
      }}
    >
      {({ isValid }): JSX.Element => (
        <Form className={classes.form}>
          <Field name="newPassword" component={NewPassword} validate={required} />
          <Field name="confirmPassword" component={ConfirmPassword} validate={required} />
          <ActionButton
            id="new-password-form-submit-button"
            type="submit"
            padding="medium"
            className={classes.confirmButton}
            disabled={!isValid}
          >
              Change Password
          </ActionButton>
        </Form>
      )}
    </Formik>
  );
};

export default NewPasswordForm;
